import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { useShallow } from 'zustand/react/shallow';
import {
  IconArrowAutofitWidth,
  IconArrowLeft,
  IconFileSearch,
  IconZoomIn,
  IconZoomOut,
} from '@rossum/ui/icons/tabler';
import { IconButton, Skeleton, SvgIcon, Tooltip } from '@rossum/ui/material';
import { useNavBarVisibility } from '../../../components/NavBar/hooks/useNavBarVisibility';
import { SearchButton } from '../../../components/NavBar/SearchButton';
import PoweredBy from '../../../components/UI/PoweredBy';
import { isEmbedded } from '../../../constants/config';
import {
  annotationSelector,
  annotationSideloadsSelector,
  pagesSelector,
} from '../../../redux/modules/annotation/selectors';
import { pauseValidation } from '../../../redux/modules/beforeLeave/actions';
import { displaySearchPanel } from '../../../redux/modules/search/actions';
import { leaveValidation } from '../../../redux/modules/ui/actions';
import { getAnnotationBacklink } from '../../../routes/useAnnotationBacklink';
import { State } from '../../../types/state';
import { PageNavigation, TranslationToggle } from '../../document-canvas';
import { DocumentTopBar } from '../../document-topbar';
import { useQuickSearchContext } from '../../quick-search/QuickSearchContext';
import { UserPanel } from '../../user-panel';
import { getBoxCoordinates } from '../document-store/documentGeometry';
import {
  useCanvasGeometryActions,
  useDocumentStore,
  useDocumentStoreContext,
} from '../document-store/DocumentStore';
import { visiblePageZustandSelector } from '../document-store/documentStoreSelectors';
import { useTranslationControls } from '../document-translation/hooks/useTranslationControls';
import { useTranslationEnabled } from '../document-translation/hooks/useTranslationEnabled';
import { DocumentContextActions } from './DocumentContextActions';
import { DocumentInformation } from './DocumentInformation';
import { ValidationTelemetryButton } from './telemetry/ValidationTelemetryButton';

const TOOLBAR_ZOOM_FACTOR = 0.2;

type AnnotationTopBarProps = {
  loading?: boolean;
};

export const AnnotationTopBar = ({
  loading = false,
}: AnnotationTopBarProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const matrixActions = useCanvasGeometryActions();

  const isTranslationEnabled = useTranslationEnabled();

  const { isActive: isTranslationActive, toggle: toggleTranslation } =
    useTranslationControls();

  // <Ex-DocumentToolbar actions>
  const handleZoomIn = useCallback(() => {
    matrixActions.zoomBy(+TOOLBAR_ZOOM_FACTOR, undefined);
  }, [matrixActions]);

  const handleZoomOut = useCallback(() => {
    matrixActions.zoomBy(-TOOLBAR_ZOOM_FACTOR, undefined);
  }, [matrixActions]);

  const handleResetZoom = useCallback(() => {
    matrixActions.resetZoom();
  }, [matrixActions]);

  const showingDocumentSearch = useSelector(
    (state: State) => state.search.shouldShow
  );
  const toggleDocumentSearch = useCallback(() => {
    dispatch(displaySearchPanel(!showingDocumentSearch));
  }, [dispatch, showingDocumentSearch]);
  // </Ex-DocumentToolbar actions>

  // <QuickSearch>
  const { setOpen } = useQuickSearchContext();

  const openQuickSearch = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  // </QuickSearch>

  // <Annotation document + queue + workspace data>
  // TODO: Get rid of Redux here
  const currentAnnotation = useSelector(annotationSelector);
  const annotationSideloads = useSelector(annotationSideloadsSelector);
  const annotationDocument = annotationSideloads.document;
  // </Annotation document + queue + workspace data>

  // <Annotation stack data>
  const annotationStack = useSelector((state: State) => state.stack);
  const totalAnnotations = annotationStack.length;
  const currentAnnotationIndex = currentAnnotation
    ? annotationStack.indexOf(currentAnnotation?.id)
    : -1;
  // </Annotation stack data>

  const { isVisible: isUserPanelVisible } = useNavBarVisibility();

  const leaveAnnotation = useCallback(() => {
    if (isEmbedded()) {
      dispatch(leaveValidation());
    } else {
      const backArrowLink = getAnnotationBacklink();
      dispatch(
        pauseValidation({
          nextAction: push(backArrowLink),
          trigger: 'cancelAnnotation',
          reason: 'surveys',
        })
      );
    }
  }, [dispatch]);

  // <Page scrolling>
  const pages = useSelector(pagesSelector);

  const { dimensions } = useDocumentStoreContext();

  const { pageNumber: visiblePageNumber } = useDocumentStore(
    useShallow(visiblePageZustandSelector(dimensions))
  );

  const handleScrollToPage = useCallback(
    (page: number) => {
      const pageDimension = dimensions.pages.find(p => p.pageNumber === page);

      if (pageDimension) {
        matrixActions.translateIntoViewport(
          getBoxCoordinates(
            page,
            {
              // a third chosen pseudorandomly so that top of the page is on top of the viewport in most cases
              // could probably be done better
              x: pageDimension.dimensions.width / 3,
              y: pageDimension.dimensions.height / 3,
              width: 42,
              height: 69,
            },
            dimensions.pages
          )
        );
      }
    },
    [dimensions.pages, matrixActions]
  );
  // </Page scrolling>

  const start = (
    <>
      <IconButton
        size="medium"
        color="secondary"
        data-cy="exit-route-button"
        onClick={leaveAnnotation}
      >
        <SvgIcon fontSize="medium">
          <IconArrowLeft />
        </SvgIcon>
      </IconButton>
      {loading ? (
        <Skeleton sx={{ width: '100%', height: '100%' }} />
      ) : (
        <>
          <DocumentInformation
            document={annotationDocument}
            annotationsInStack={totalAnnotations}
            currentAnnotationIndex={currentAnnotationIndex}
          />
          <DocumentContextActions
            id="document-context-actions-button"
            document={annotationDocument}
            annotation={currentAnnotation}
          />
        </>
      )}
    </>
  );

  const center = loading ? (
    <Skeleton sx={{ width: 320, height: 24 }} />
  ) : (
    <>
      <PageNavigation
        currentPage={visiblePageNumber}
        totalPages={pages.length}
        onNavigate={handleScrollToPage}
      />
      <Tooltip
        title={intl.formatMessage({
          id: 'components.topbarV2.actions.zoomIn',
        })}
      >
        <IconButton size="medium" color="secondary" onClick={handleZoomIn}>
          <SvgIcon fontSize="medium">
            <IconZoomIn />
          </SvgIcon>
        </IconButton>
      </Tooltip>
      <Tooltip
        title={intl.formatMessage({
          id: 'components.topbarV2.actions.zoomOut',
        })}
      >
        <IconButton size="medium" color="secondary" onClick={handleZoomOut}>
          <SvgIcon fontSize="medium">
            <IconZoomOut />
          </SvgIcon>
        </IconButton>
      </Tooltip>
      <Tooltip
        title={intl.formatMessage({
          id: 'components.topbarV2.actions.resetZoom',
        })}
      >
        <IconButton size="medium" color="secondary" onClick={handleResetZoom}>
          <SvgIcon fontSize="medium">
            <IconArrowAutofitWidth />
          </SvgIcon>
        </IconButton>
      </Tooltip>
      <TranslationToggle
        visible={isTranslationEnabled}
        isActive={isTranslationActive}
        onToggle={toggleTranslation}
      />
      <Tooltip
        title={intl.formatMessage({
          id: 'components.topbarV2.actions.find',
        })}
      >
        <IconButton
          size="medium"
          color="secondary"
          onClick={toggleDocumentSearch}
        >
          <SvgIcon fontSize="medium">
            <IconFileSearch />
          </SvgIcon>
        </IconButton>
      </Tooltip>
      <ValidationTelemetryButton />
    </>
  );

  const end = (
    <>
      <PoweredBy />
      {!isEmbedded() && <SearchButton onClick={openQuickSearch} />}
      {isUserPanelVisible ? <UserPanel /> : null}
    </>
  );

  return <DocumentTopBar start={start} center={center} end={end} />;
};
